import { useRef, useEffect } from 'react';
import {
  Stack,
  Box,
} from '@chakra-ui/core';
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { TabForm } from '../../../../common/components/Tabs';
import { FormSection, FormSubmitButton } from '../../../../common/components/Form/Form';
import {
  RadioGroup,
  RadioInput,
  CheckboxGroup,
  CheckboxGroupInput,
  CheckBoxInput,
  Label,
  TextInput,
} from '../../../../common/components/Form/FormInputs';
import { DateInput } from '../../../../common/components/Form/FormDateInput';
import {
  QuoteUploadFilename,
  QuoteUploadWrapper,
} from '../../../../common/components/QuoteUpload/QuoteUpload';
import { BlocFormik } from '../../../../common/components/Form/BlocFormik';

const TAB_SECTIONS = [
  {
    title: 'Informations générales',
    fields: ['adaptableHousing', 'hasTechnicalSheet', 'affectedRooms', 'affectedHabits'],
  },
  {
    title: 'Préconisations',
    fields: [
      'works',
      'isTechnicalAssistanceRequired',
      'isWheelchairOrOtherEquipmentsRequired',
      'isSpecificVehicleArrangementsRequired',
      'hasLifestyleAdviceWithEquipmentIntegration',
      'humanHelpOrService',
    ],
  },
  {
    title: 'Prise en compte de la LPPR',
    fields: ['hasLpprTechnicalRecommandation'],
  },
];

export const BlocATab2 = ({
  blocA: { error, pending, diagnosticSummary, folder = {}, ...initialValues },
  onSubmit,
}) => {
  const formikRef = useRef();

  useEffect(() => {
    if (formikRef.current) {
      formikRef.current.setErrors(error && error.errors);
    }
  }, [error]);

  useEffect(() => {
    if (
      formikRef.current && 
      (folder.ergoInterventionType === 'AMOc - devis existant' ||
      folder.ergoInterventionType === 'AMOc - facture existante' ||
      folder.ergoInterventionType === 'AMOc - diagnostic devis existant')
    ) {
      formikRef.current.setFieldValue('worksQuoteReceived', 'true');
    }
  }, [folder.ergoInterventionType]);

  return (
    <BlocFormik
      innerRef={formikRef}
      initialValues={initialValues}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={(values, { setTouched, setSubmitting }) => {
        return onSubmit({ ...initialValues, folder, diagnosticSummary, ...values }).then(() => {
          setTouched({});
          setSubmitting(false);
        });
      }}
    >
      {formik => (
        <TabForm onSubmit={formik.handleSubmit}>
          {diagnosticSummary && (
            <Stack
              spacing="3.5rem"
              css={{
                '[role=group], [role=radiogroup]': {
                  paddingLeft: '1.5rem',
                },
                'fieldset:not(:first-of-type)': {
                  marginTop: '2.5rem',
                },
              }}
            >
              {TAB_SECTIONS.map(section => (
                <FormSection key={section.title} title={section.title}>
                  {section.fields.map(fieldName => (
                    <DiagnosticSummaryInput
                      key={fieldName}
                      name={fieldName}
                      content={diagnosticSummary[fieldName]}
                    />
                  ))}
                </FormSection>
              ))}
            </Stack>
          )}

          <FormSection title="Réception des devis de travaux" mt="2.5rem">
            <RadioGroup
              name="worksQuoteReceived"
              legend="Un devis travaux a-t-il été reçu ?"
              disabled={
                (!initialValues.typeOfVisit?.match(/(joint|existing_quote)/) ||
                  !initialValues.visitReminderSentAt || 
                  initialValues.ergoInterventionType === 'AMOc - ergo seul' || 
                  initialValues.ergoInterventionType === 'AMOc - diagnostic ergo seul' ||
                  initialValues.ergoInterventionType === 'AMOc - devis existant' ||
                  initialValues.ergoInterventionType === 'AMOc - facture existante' ||
                  initialValues.ergoInterventionType === 'AMOc - diagnostic devis existant'
                ) &&
                !initialValues.firstQuoteVersion?.downloadLink
              }
            >
              <RadioInput 
                value="true" 
                disabled={pending}
                checked={
                  folder.ergoInterventionType === 'AMOc - devis existant' ||
                  folder.ergoInterventionType === 'AMOc - facture existante' ||
                  folder.ergoInterventionType === 'AMOc - diagnostic devis existant'
                }
              >
                Oui
              </RadioInput>
              {formik.values?.worksQuoteReceived === 'true' ? (
                <Box py={4} ml="1.75rem" pl="1.75rem">
                  {formik.values.typeOfVisit?.match(/(joint|existing_quote)/) &&
                  formik.values.positionedCompany ? (
                    <Box mb={6}>
                      <Label>Devis</Label>
                      <QuoteUploadWrapper>
                        <QuoteUploadFilename
                          quoteVersion={initialValues.firstQuoteVersion}
                          placeholder="Aucun devis disponible"
                        />
                      </QuoteUploadWrapper>
                    </Box>
                  ) : null}
                  <DateInput
                    label="1ère date de reception"
                    name="worksQuoteReceivedAt"
                    id="worksQuoteReceivedAt"
                    disabled={!initialValues.typeOfVisit.match(/(joint|existing_quote)/)}
                  />

                  <TextInput name="worksQuoteComment" display="none" />

                  <DateInput
                    label="Date de demandes complémentaires"
                    name="worksQuoteAdditionalRequestsSentAt"
                    id="worksQuoteAdditionalRequestsSentAt"
                    style={{ mb: 6 }}
                    readOnly
                  />
                </Box>
              ) : null}
              <RadioInput value="false" disabled={pending}>
                Non
              </RadioInput>
            </RadioGroup>
          </FormSection>

          <FormSubmitButton
            type="submit"
            disabled={pending}
            isLoading={formik.isSubmitting}
            loadingText="Enregistrer"
          >
            Enregistrer
          </FormSubmitButton>
        </TabForm>
      )}
    </BlocFormik>
  );
};

const DiagnosticSummaryInput = ({ name, content }) => {
  switch (content.type) {
    case 'simple_choice_radio':
      return (
        <RadioGroup isInline name={name} legend={content.title}>
          {Object.entries(content.availableAnswers).map(([value, label]) => (
            <RadioInput key={label} value={value}>
              {label}
            </RadioInput>
          ))}
        </RadioGroup>
      );
    case 'multiple_choice':
      return (
        <CheckboxGroup name={name} legend={content.title}>
          {Object.entries(content.availableAnswers).map(([value, label]) => (
            <CheckboxGroupInput key={value} value={value.toUpperCase()}>
              {label}
            </CheckboxGroupInput>
          ))}
        </CheckboxGroup>
      );
    case 'simple_choice_checkbox':
      return (
        <Box
          mb={5}
          mt={name === 'isTechnicalAssistanceRequired' ? 8 : 0}
          css={{ span: { fontWeight: 600 } }}
        >
          <CheckBoxInput name={name}>{content.title}</CheckBoxInput>
        </Box>
      );
    default:
      return null;
  }
};
